// Supabase DB row(snake_case) → DataContext 형식(camelCase) 변환 헬퍼

import { reportError } from './sentry.js'
import { isTransientFetchMessage } from './supabaseRetry.js'

export const toUser = (row) => ({
  id: row.id,
  loginId: row.login_id,
  name: row.name,
  role: row.role,
  school: row.school ?? '',
  grade: row.grade ?? '',
  className: row.class_name ?? '',
  gender: row.gender ?? null,
  // 비밀번호(해시 포함)는 앱 모델에 싣지 않는다 — localStorage 세션에 노출되기 때문.
  phone: row.phone ?? '',
  parentPhone: row.parent_phone ?? '',
  passwordChangedAt: row.password_changed_at ?? null,
  selfIndex: row.self_index ?? 70,
  riskLevel: row.risk_level ?? 'normal',
  status: row.status ?? 'active',
  enrolledAt: row.enrolled_at ?? null,
  withdrawnAt: row.withdrawn_at ?? null, // 퇴원 처리일 (학생 목록 보고서 표시용)
  cancelledAt: row.cancelled_at ?? null, // 신청취소 처리일 (학생 목록 보고서 표시용)
  subject: row.subject ?? '',
  workSchedule: row.work_schedule ?? '', // 교직원 업무일정 — 보고서 헤더 기본값
  groups: row.group_names ?? [], // 소속 그룹 (빈 배열 = 무소속: 직원이면 전체 열람, 학생이면 공용)
  studyJournals: toArray(row.study_journals), // 학습일지 첨부 메타 [{ path, name, size, uploadedAt }]
})

export const toMindRecord = (row) => ({
  id: row.id,
  studentId: row.student_id,
  date: row.date,
  mood: row.mood,
  motivation: row.motivation,
  confidence: row.confidence,
  memo: row.memo ?? '',
})

export const toDiaryRecord = (row) => ({
  id: row.id,
  studentId: row.student_id,
  date: row.date,
  praise: row.praise ?? '',
  reflection: row.reflection ?? '',
  resolution: row.resolution ?? '',
})

export const toLearningRecord = (row) => ({
  id: row.id,
  studentId: row.student_id,
  date: row.date,
  subject: row.subject,
  duration: row.duration,
  actualDuration: row.actual_duration ?? row.duration ?? null,
  recordType: row.record_type ?? 'timer',
  status: row.status ?? 'done',
  studyMethod: row.study_method ?? '',
  studyLocation: row.study_location ?? '',
  content: row.content ?? '',
  plannedMin: row.planned_min ?? null,
  sortOrder: row.sort_order ?? null,
  focus: row.focus,
  startTime: toHHMM(row.start_time),
})

// TIME 컬럼은 'HH:MM:SS'로 오므로 'HH:MM'으로 자른다
function toHHMM(t) { return t ? String(t).slice(0, 5) : null }

export const toAttendanceRecord = (row) => ({
  id: row.id,
  studentId: row.student_id,
  date: row.date,
  status: row.status ?? 'present',
  checkInAt: row.check_in_at ?? null,
  checkOutAt: row.check_out_at ?? null,
  scheduledArrival: toHHMM(row.scheduled_arrival),
  scheduledDeparture: toHHMM(row.scheduled_departure),
  checkoutStatus: row.checkout_status ?? null,
  source: row.source ?? 'kiosk',
  note: row.note ?? '',
  events: row.events ?? [],
})

export const toAttendanceSchedule = (row) => ({
  id: row.id,
  studentId: row.student_id,
  dayOfWeek: row.day_of_week,
  arrivalTime: toHHMM(row.arrival_time),
  departureTime: toHHMM(row.departure_time),
})

export const toAttendanceNotification = (row) => ({
  id: row.id,
  studentId: row.student_id,
  date: row.date,
  type: row.type,
  message: row.message,
  resolved: row.resolved ?? false,
  createdAt: row.created_at,
})

export const toTask = (row) => ({
  id: row.id,
  studentId: row.student_id,
  title: row.title,
  subject: row.subject,
  dueDate: row.due_date,
  dueTime: row.due_time ?? '23:59',
  status: row.status ?? 'pending',
  assignerId: row.assigner_id ?? null,
  assignerName: row.assigner_name ?? '',
  method: row.method ?? '',
  content: row.content ?? '',
  attachments: row.attachments ?? [], // 강사 첨부 (문제/자료)
  submissions: row.submissions ?? [], // 학생 제출 파일
  createdAt: row.created_at ?? null, // 새 과제 배지 판정 기준
})

export const toCounselingRecord = (row) => ({
  id: row.id,
  studentId: row.student_id,
  educatorId: row.manager_id,
  date: row.date,
  comment: row.content,
  type: row.type ?? 'study',
  targetType: row.target_type ?? 'student',
  // 보고서 양식 6단계 — 구 기록은 null(단일 comment만 존재)
  topic: row.topic ?? '',
  diagnosis: row.diagnosis ?? '',
  advice: row.advice ?? '',
  followUp: row.follow_up ?? '',
  note: row.note ?? '',
  nextAppointment: row.next_appointment ?? '',
  startTime: row.start_time ?? '',
  endTime: row.end_time ?? '',
  attachments: toArray(row.attachments), // [{ path, name, size }] — PDF 첨부 메타
})

// 지정예약 완료 상담(booking_records) → 상담보고 표시용 정규화.
// 최인선 사례(2026-08-01): 예약 시스템으로만 컨설팅을 기록하는 강사가 있어
// 상담보고 리스트·월간 보고서·통계가 counseling_records와 합쳐 소비한다.
// source: 'booking' — 상담보고 화면에서 수정·삭제 금지(예약 화면이 원본).
// 유형은 프로그램명으로 추정: '진로' 포함 → 진로진학, 그 외 → 교과학습.
// 시간은 지도보고서에 작성된 실제 상담 시간(start_time/end_time) 우선, 미입력(구 기록)은
// 예약 슬롯 시간 폴백 — "집계는 예약 기준이 아니라 작성 결과 기준"(2026-08-20 클라).
export const toBookingCounselingRecord = (row) => {
  const slot = row.booking_reservations?.booking_slots ?? null
  const hhmm = (v) => (v ? String(v).slice(0, 5) : '')
  const programName = row.booking_programs?.name ?? ''
  return {
    id: row.id,
    studentId: row.student_id,
    educatorId: row.educator_id,
    date: row.date,
    comment: '',
    type: programName.includes('진로') ? 'career_path' : 'subject_learning',
    targetType: 'student',
    topic: row.topic ?? '',
    diagnosis: row.diagnosis ?? '',
    advice: row.advice ?? '',
    followUp: row.follow_up ?? '',
    note: row.note ?? '',
    nextAppointment: row.next_appointment ?? '',
    startTime: hhmm(row.start_time) || hhmm(slot?.start_time),
    endTime: hhmm(row.end_time) || hhmm(slot?.end_time),
    attachments: [],
    source: 'booking',
  }
}

export const toAlert = (row) => ({
  id: row.id,
  studentId: row.student_id,
  managerId: row.manager_id,
  type: row.type ?? 'mind',
  level: row.severity ?? 'warning',
  message: row.message,
  detail: row.detail ?? '',
  date: row.date,
  resolved: row.resolved ?? false,
  coachingComment: row.coaching_comment ?? '',
})

export const toTodoItem = (row) => ({
  id: row.id,
  studentId: row.student_id,
  date: row.date,
  subject: row.subject,
  plannedMin: row.planned_min,
  content: row.content ?? '',
  done: row.done ?? false,
})

export const toCareerDesignResult = (row) => ({
  id: row.id,
  studentId: row.student_id,
  date: row.date,
  selectedVerbs: row.selected_verbs ?? [],
  selectedActivities: row.selected_activities ?? [],
  selectedCategories: row.selected_categories ?? [],
  primaryCat: row.primary_cat ?? '',
  typeName: row.type_name ?? '',
  finalScores: row.final_scores ?? {},
  fields: row.fields ?? [],
})

export const toLearningDiagnosisResult = (row) => ({
  id: row.id,
  studentId: row.student_id,
  date: row.date,
  answers: row.answers ?? [],
  domainScores: row.domain_scores ?? {},
  stageScores: row.stage_scores ?? {},
  stageGrades: row.stage_grades ?? {},
  stateTypes: row.state_types ?? {},
  typeName: row.type_name ?? '',
})

export const toParentChild = (row) => ({
  id: row.id,
  parentId: row.parent_id,
  studentId: row.student_id,
})

export const toDailySelfScore = (row) => ({
  id: row.id,
  studentId: row.student_id,
  date: row.date,
  score: row.score,
  memo: row.memo ?? '',
})

// jsonb 컬럼은 이미 배열로 오지만, 문자열로 저장된 경우도 방어한다
const toArray = (v) => {
  if (Array.isArray(v)) return v
  if (typeof v === 'string') {
    try { return JSON.parse(v) } catch { return [] }
  }
  return []
}

export const toWorkPlan = (row) => ({
  id: row.id,
  authorId: row.author_id,
  planDate: row.plan_date,
  planTime: row.plan_time ?? '',
  planEndTime: row.plan_end_time ?? '',
  types: toArray(row.types),
  studentIds: toArray(row.student_ids), // 구 기록 호환 (2026-07-30 개편 후 신규는 빈 배열)
  audiences: toArray(row.audiences), // 대상 복수선택 — WORK_PLAN_AUDIENCES 키
  memo: row.memo ?? '',
  status: row.status ?? 'planned',
  createdAt: row.created_at,
})

export const toManagementReport = (row) => ({
  id: row.id,
  authorId: row.author_id,
  date: row.date,
  workType: row.work_type ?? 'etc',
  startTime: row.start_time ?? '',
  endTime: row.end_time ?? '',
  content: row.content ?? '',
  note: row.note ?? '',
  groupName: row.group_name ?? null, // null = 공용 (그룹 도입 전 기록)
  createdAt: row.created_at,
})

export const toFinanceRecord = (row) => ({
  id: row.id,
  authorId: row.author_id,
  date: row.date,
  category: row.category ?? 'etc',
  itemName: row.item_name ?? '',
  unitPrice: row.unit_price ?? 0,
  quantity: row.quantity ?? 1,
  amount: row.amount ?? 0,
  vendor: row.vendor ?? '',
  paymentMethod: row.payment_method ?? 'personal_card',
  attachments: toArray(row.attachments), // [{ path, name, size }] — 영수증·사진 메타
  groupName: row.group_name ?? null,
  createdAt: row.created_at,
})

export const toLessonReport = (row) => ({
  id: row.id,
  authorId: row.author_id,
  date: row.date,
  studentIds: toArray(row.student_ids),
  startTime: row.start_time ?? '',
  endTime: row.end_time ?? '',
  topic: row.topic ?? '',
  textbook: row.textbook ?? '',
  content: row.content ?? '',
  homework: row.homework ?? '',
  note: row.note ?? '',
  createdAt: row.created_at,
})

export const toUrgentReport = (row) => ({
  id: row.id,
  authorId: row.author_id,
  content: row.content,
  groupName: row.group_name ?? null,
  confirmed: row.confirmed ?? false,
  confirmedBy: row.confirmed_by ?? null,
  confirmedAt: row.confirmed_at ?? null,
  createdAt: row.created_at,
})

// flat 형식 [{ educatorId, studentId }] — 기존 컴포넌트 코드와 호환
export const toAssignment = (row) => ({
  educatorId: row.educator_id,
  studentId: row.student_id,
})

export const toQuizSet = (row) => ({
  id: row.id,
  title: row.title,
  grade: row.grade,
  subject: row.subject ?? '국어',
  round: row.round ?? 1,
  source: row.source ?? '',
  description: row.description ?? '',
  isPublished: row.is_published ?? true,
  isScoreOnly: row.is_score_only ?? false,
  maxScore: row.max_score ?? null,
  createdAt: row.created_at,
})

export const toQuizQuestion = (row) => ({
  id: row.id,
  quizSetId: row.quiz_set_id,
  orderNo: row.order_no,
  type: row.type ?? 'short',
  question: row.question,
  acceptedAnswers: row.accepted_answers ?? [],
  explanation: row.explanation ?? '',
  hint: row.hint ?? '',
  points: row.points ?? 1, // 배점 — 컬럼 미적용/기존 문항은 1점
  attachments: row.attachments ?? [],
})

export const toQuizAttempt = (row) => ({
  id: row.id,
  studentId: row.student_id,
  quizSetId: row.quiz_set_id,
  answers: row.answers ?? [],
  score: row.score ?? 0,
  total: row.total ?? 0,
  submittedAt: row.submitted_at,
})

// 센터 휴무기간 (방학·임시휴무) — 기간 내 자동 결석 판정 중단 (scripts/add-center-closures.sql)
export const toCenterClosure = (row) => ({
  id: row.id,
  startDate: row.start_date,
  endDate: row.end_date,
  label: row.label ?? '',
  createdBy: row.created_by ?? null,
  createdAt: row.created_at,
})

// 학생 피드백(수시 코멘트) — 상담 기록과 별개의 가벼운 코멘트 (scripts/add-student-feedback.sql)
export const toStudentFeedback = (row) => ({
  id: row.id,
  studentId: row.student_id,
  authorId: row.author_id ?? null,
  authorName: row.author_name ?? '',
  date: row.date,
  content: row.content ?? '',
  createdAt: row.created_at,
})

export const toNotice = (row) => ({
  id: row.id,
  kind: row.kind ?? 'notification',
  audience: row.audience ?? 'all',
  title: row.title ?? '',
  content: row.content ?? '',
  createdBy: row.created_by ?? null,
  createdByName: row.created_by_name ?? '',
  active: row.active ?? true,
  createdAt: row.created_at,
})

// ── 침묵 실패 방지: fetch 결과 검사 ──────────────────────────────
// Supabase 쿼리 결과에서 에러를 errors 배열에 수집하고 data 행을 꺼낸다.
// 에러가 나도 throw하지 않고 [] 를 돌려줘 부분 렌더는 유지하되,
// "실패했다는 사실"은 errors 에 남겨 화면에서 경고할 수 있게 한다.
// 동시에 Sentry로도 보내 개발자가 운영 중 즉시 인지하게 한다.
export function collectRows(res, table, errors) {
  if (res?.error) {
    const message = res.error.message ?? String(res.error)
    const transient = isTransientFetchMessage(message)
    errors.push({ table, message, code: res.error.code ?? null, transient })
    // 일시적 네트워크 실패는 여기서 보고하지 않는다 — 상위(DataContext)가 백오프
    // 재시도를 소진한 뒤 reportFinalFetchFailure로 1건만 보고한다. 재시도로
    // 복구된 블립까지 시도마다 Sentry에 쌓이던 잡음(NAMEKE-8)의 원인이었다.
    if (!transient) reportError(res.error, { where: 'fetch', table })
    return []
  }
  return res?.data ?? []
}

// 재시도 소진 후에도 남은 일시적 fetch 실패들을 1건으로 묶어 보고.
// where 'fetch' + 첫 에러 code를 유지해 기존 Sentry 이슈
// (fingerprint [where, code])와 같은 이슈로 이어진다.
export function reportFinalFetchFailure(fetchErrors, extra) {
  const transient = (fetchErrors ?? []).filter((e) => e.transient)
  if (!transient.length) return
  const captured = Object.assign(new Error(transient[0].message), {
    name: 'SupabaseError',
    code: transient[0].code ?? undefined,
  })
  reportError(captured, {
    where: 'fetch',
    tables: transient.map((e) => e.table),
    ...(extra ?? {}),
  })
}

// ── 내신백서 (add-exam-reports.sql) ─────────────────────────
export const toExamFeedback = (row) => ({
  id: row.id,
  studentId: row.student_id,
  examYear: row.exam_year,
  semester: row.semester,
  examType: row.exam_type,
  school: row.school ?? '',
  grade: row.grade ?? '',
  className: row.class_name ?? '',
  writtenDate: row.written_date ?? null,
  subjects: toArray(row.subjects),
  actionPlan: row.action_plan ?? '',
  updatedBy: row.updated_by ?? null,
  updatedByName: row.updated_by_name ?? '',
  createdAt: row.created_at,
  updatedAt: row.updated_at ?? row.created_at,
})

export const toExamAnalysis = (row) => ({
  id: row.id,
  school: row.school,
  grade: row.grade,
  subject: row.subject,
  examYear: row.exam_year,
  semester: row.semester,
  examType: row.exam_type,
  examDate: row.exam_date ?? null,
  startTime: row.start_time ?? '',
  endTime: row.end_time ?? '',
  publisher: row.publisher ?? '',
  totalQuestions: row.total_questions ?? null,
  difficulty: row.difficulty ?? '',
  questionTypes: toArray(row.question_types),
  scope: row.scope ?? '',
  sections: row.sections && typeof row.sections === 'object' && !Array.isArray(row.sections) ? row.sections : {},
  attachments: toArray(row.attachments),
  authorId: row.author_id ?? null,
  updatedBy: row.updated_by ?? null,
  updatedByName: row.updated_by_name ?? '',
  createdAt: row.created_at,
  updatedAt: row.updated_at ?? row.created_at,
})

export const toExamBestCase = (row) => ({
  id: row.id,
  studentId: row.student_id,
  authorId: row.author_id ?? null,
  authorName: row.author_name ?? '',
  examYear: row.exam_year,
  semester: row.semester,
  examType: row.exam_type,
  subject: row.subject ?? '',
  score: row.score ?? '',
  prepPeriod: row.prep_period ?? '',
  process: row.process ?? '',
  keyPoint: row.key_point ?? '',
  createdAt: row.created_at,
  updatedAt: row.updated_at ?? row.created_at,
})
